import * as React from 'react';
import { observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';

import { MtsTicket, RateChangeAcceptType, TicketTipsContainer, TicketStatusType, OfferType } from 'eTip--common';
import { RateAdjustmentOptions, NumberInputForStake, PresetStakes, CountDownLoader, AcceptOrReject, AMLOptionsComponent, TicketAndCouponHint, InfoBox } from 'common--design-library';
import { TicketErrors } from 'desktop--design-library';
import { currencyFormatter, translationForMts } from 'easit--localization';
import * as classNames from 'classnames';

export interface IOTBTicketContainerProps {
    ticket: Instance<typeof MtsTicket>;
    toggleOTB: (isOn: boolean) => void;
}

export const OTBTicketContainer = observer((props: IOTBTicketContainerProps) => {

    const renderErrors = () => {
        return (ticket.errors && ticket.errors.length || ticket.errorsAboveSubmitButtons && ticket.errorsAboveSubmitButtons.length) && <div className="otb-active-bordered">
            <TicketErrors errors={ticket.errors?.map(error => error)} removeMargins={true} />
            <TicketErrors errors={ticket.errorsAboveSubmitButtons?.map(error => error)} removeMargins={true} />
        </div>
    }

    const renderClearTicket = () => {
        return <div className="pay-buttons otb-background text-center p-2">
            <span className="cursor-pointer" onClick={() => { props.toggleOTB(true); ticket.clearCouponAsync(); return false; }}><i className="fas fa-trash-alt ml-auto cursor-pointer" /> {t.otb_CancelBetslip}</span>
        </div>
    }

    const ticket = props.ticket;
    const t = translationForMts();
    return <div className={classNames("otb-wrapper", ticket.isOTBSettingSet && "otb-active")}>

        {
            ticket.isOTBSettingSet
                ? <>
                    {
                        ticket.ticketState === TicketStatusType.Hint
                            ? <TicketAndCouponHint
                                onAcceptOrReject={(hintAccepted) => ticket.couponPayWithHintAsync(hintAccepted)}
                                hint={ticket.hintForUser}
                                currencyIsMoney={ticket.isStakeAsMoney}
                                isAcceptingHintInProgress={ticket.isAcceptingHintInProgress}
                                className="otb-active-bordered"
                                removeMargins={true}
                                canSwapPrimarySecondaryForButtons={false}
                            />
                            : <div>
                                <div className="p-2 otb-background">
                                    {t.stake} {currencyFormatter.format(ticket.stake.value)}
                                    {
                                        ticket.rateChangeAcceptType === RateChangeAcceptType.AnyRate &&
                                        <span>, {t.coupon_liveAcceptAllRates}</span>
                                    }
                                    {
                                        ticket.rateChangeAcceptType === RateChangeAcceptType.RateIncrease &&
                                        <span>, {t.coupon_liveAcceptHigerRate}</span>
                                    }
                                </div>
                                {
                                    ticket.ticketState === TicketStatusType.New && ticket.amlOptions && ticket.amlOptions.items.length > 0 &&
                                    <div className="otb-active-bordered p-2"><AMLOptionsComponent
                                        items={[...ticket.amlOptions.items]}
                                        toggleOption={ticket.amlOptions.toggleAMLOption}
                                        removeMargins={true}
                                        updateDisabled={ticket.amlOptions.updateDisabled}
                                    />
                                    </div>
                                }
                                {
                                    <TicketTipsContainer
                                        tips={[...ticket.selections]}
                                        removeTip={ticket.removeTipAsync}
                                        changeIsBanker={ticket.setIsBankerAsync}
                                        isEnabledUI={ticket.isEnabledUI}
                                        onGoToDetail={null}
                                        canShowBanker={ticket.isSystem}
                                        canShowAliasNumber={ticket.canShowAliasNumber}
                                        isForOneTapBet={true}
                                    />
                                }
                                <div style={{ display: "none" } /* otherwise changes of tip rate are not propagated. I don't know why */}>{ticket.rate}</div>
                                {renderErrors()}

                                {
                                    !(ticket.ticketState === TicketStatusType.Accepted || ticket.ticketState === TicketStatusType.Rejected || ticket.ticketState === TicketStatusType.Pay) && ticket.selectionsCount > 0 &&
                                    <>
                                        <div className={classNames("pay-buttons d-flex otb-active-bordered-top", __IsMTSTipos__ ? "mt-2" : null)}>
                                            <button className={classNames("btn  flex-half", __IsMTSTipos__ ? "btn-primary" : "btn-secondary")} disabled={ticket.areRatesUpToDate} onClick={() => { ticket.isEnabledUI && ticket.acceptChangedRatesAndEvaluateAsync() }}>
                                                {ticket.areRatesUpToDate || ticket.errors && ticket.errors.length > 0
                                                    ? t.coupon_evaluateBtn
                                                    : t.coupon_acceptRateChangeBtn /* same in ticketStandardAndLive and ticket.tsx and otbTickerContainer */}
                                                {
                                                    ticket.isAcceptChangedRatesAndEvalInProgress ? <span className="fas fa-spinner fa-spin"></span> : ''
                                                }
                                            </button>
                                            <button className={classNames("btn flex-half", __IsMTSTipos__ ? "btn-success" : "btn-primary")} disabled={!ticket.areRatesUpToDate}
                                                onClick={() => { if (ticket.isEnabledUI && ticket.areRatesUpToDate) { ticket.ticketPayAsync(); } }} data-test-role="ticket-submit"
                                            >
                                                {t.coupon_sendToPay}
                                                {
                                                    /*(ticket.ticketState === TicketStatusType.Pay) &&
                                                    <>
                                                        <span className="fas fa-spinner fa-spin ml-2"></span>
                                                        {
                                                            ticket.offerType == OfferType.Live &&
                                                            <span className="ml-2"> <CountDownLoader cnt={ticket.visualDelay} /></span>
                                                        }
                                                    </>*/
                                                }
                                            </button>
                                        </div>
                                        {renderClearTicket()}
                                    </>
                                }

                                {
                                    (ticket.ticketState === TicketStatusType.Accepted || ticket.ticketState === TicketStatusType.Rejected) &&
                                    <div className="otb-active-bordered otb-active-bordered-bottom2">
                                        <AcceptOrReject
                                            clearCoupon={null}
                                            prepareJustSentCouponCopy={null}
                                            isAccepted={ticket.ticketState === TicketStatusType.Accepted}
                                            allowTicketCopy={false}
                                            isTicketCopyInProgress={false}
                                            hideButtons={true}
                                            removeMargins={true}
                                            canSwapPrimarySecondary={false}
                                        />
                                    </div>

                                }

                                {
                                    ticket.ticketState === TicketStatusType.Pay &&
                                    <div className={classNames("px-2 pt-0 pb-2 text-center otb-background", __IsMTSTipos__ ? "mt-2" : null)}>
                                        {t.otb_Sending}
                                        <span className="fas fa-spinner fa-spin ml-2"></span>
                                        {
                                            ticket.offerType == OfferType.Live &&
                                            <span className="ml-2"> <CountDownLoader cnt={ticket.visualDelay} /></span>
                                        }
                                    </div>
                                }
                            </div>
                    }
                </>
                : <>
                    <div className="p-2 otb-background">
                        <div className="info-text">{t.otb_InfoText}</div>
                        <div className="mt-3"><InfoBox messages={[{ text: t.otb_InfoWarning, withIcon: true }]} type={__IsMTSTipos__ ? "warning" : "info"} removeMargins={true} /></div>
                        <div className="mt-3">{t.otb_SettingHeader}</div>
                        <div className="ticket-summary-item mt-3 stake d-flex justify-content-between align-items-center">
                            <span className="label-text">{t.coupon_Stake}:</span>
                            <div className={__IsMTSTipos__ ? "d-flex align-items-center stake-input-wrapper" : null}>
                                <NumberInputForStake
                                    className={classNames("ml-1 stake-input", __IsMTSTipos__ ? "text-center" : "text-right mr-1")}
                                    value={ticket.stake.value}
                                    canUseDecimals={ticket.useDecimalPlacesForStake}
                                    onChange={(isValid, value) => { if (ticket.isEnabledUI) { ticket.setStakeOrValidity(isValid, value); } }}
                                    onBlur={() => { ticket.isEnabledUI && ticket.stakeChangedAsync(); }}
                                    disabled={!ticket.isEnabledUI}
                                    roleForTest={"total-stake"}
                                />
                                <span className="currency-placeholder">{ticket.currency}</span>
                            </div>
                        </div>
                        <PresetStakes presetStakes={ticket.stakesFix}
                            isEnabledUI={ticket.isEnabledUI}
                            maxStake={ticket.maxStake}
                            setStake={(stake) => { ticket.setStakeOrValidity(true, stake) }}
                            onStakeChaned={ticket.stakeChangedAsync}
                            isFreeTicketAccountTypeSelected={false} //TODO_FT: Chceme implementovat OTB pro FreeTicket? nechceme!
                        />

                        <RateAdjustmentOptions
                            selectedOption={ticket.rateChangeAcceptType}
                            selectOption={(selectedOption) => ticket.setOrToggleAcceptedRateChangeAsync(selectedOption)}
                            isPercentAllowed={false}
                            isEnabledUI={ticket.isEnabledUI}
                        />
                    </div>
                    {renderErrors()}
                    <div className={classNames("pay-buttons", __IsMTSTipos__ ? null : "d-flex")}>
                        <button className={classNames("btn", __IsMTSTipos__ ? "btn-primary" : "btn-secondary flex-half")} onClick={() => { props.toggleOTB(false); }}>{t.otb_FirstPage_Cancel}</button>
                        <button className={classNames("btn", __IsMTSTipos__ ? "btn-success" : "btn-primary flex-half")}
                            onClick={() => { ticket.setIsOTBSettingSet(true); }}
                        >
                            {t.otb_FirstPage_Confirm}
                            {
                                ticket.ticketState === TicketStatusType.Saving &&
                                <span className="fas fa-spinner fa-spin ml-2"></span>
                            }
                        </button>
                    </div>
                </>
        }
    </div >
});